// Página pública del evento. Un solo componente para los 5 estilos: el estilo decide la estructura
// (split / stack / poster / band), los tokens deciden el color y la tipografía. En escritorio siempre hay dos
// columnas (la lateral lleva la inscripción); en celular la inscripción va antes del contenido.
// Responsive por consulta de contenedor, así se ve igual en la vista previa del organizador y en la página real.
import type { CSSProperties, ReactNode } from 'react';
import { Button, Chip, Progress } from './components.tsx';
import { EventBackground } from './EventBackground.tsx';
import { EventCover } from './EventCover.tsx';
import { formatEventDates, zoneLabel } from './format.ts';
import { IconCalendar, IconPin, IconPoll, IconQuestion, IconShare, IconWords } from './icons.tsx';
import { resolveEventTheme, type ThemeInput } from './tokens.ts';

export type RegistrationState = 'open' | 'full' | 'closed' | 'cancelled' | 'past';
export type ParticipationTool = 'poll' | 'questions' | 'wordcloud';

export interface EventPageData {
  title: string;
  kicker?: string | null;
  summary?: string | null;
  /** null = fecha por confirmar (borrador de la IA). */
  startsAt: string | null;
  endsAt: string | null;
  timeZone: string;
  venue: { name: string | null; address: string | null; note?: string | null; mapUrl?: string | null };
  organizer: { name: string; tagline?: string | null; initials: string };
  about: string[];
  agenda: { time: string; title: string; detail?: string | null }[];
  participation: ParticipationTool[];
  accessibility: string[];
  capacity: { total: number; taken: number } | null;
  registration: RegistrationState;
  coverImageUrl?: string | null;
  privacyUrl: string;
}

export interface EventPageProps {
  event: EventPageData;
  theme: ThemeInput;
  /** Fondo animado (el organizador lo apaga con un interruptor). La preferencia de menos movimiento siempre gana. */
  motion?: boolean;
  onRegister?: () => void;
  onShare?: () => void;
}

const TOOL_LABEL: Record<ParticipationTool, { label: string; icon: ReactNode }> = {
  poll: { label: 'Votación en vivo', icon: <IconPoll size={16} /> },
  questions: { label: 'Preguntas al panel', icon: <IconQuestion size={16} /> },
  wordcloud: { label: 'Nube de palabras', icon: <IconWords size={16} /> },
};

export function EventPage({ event, theme, motion = true, onRegister, onShare }: EventPageProps) {
  const t = resolveEventTheme(theme);
  const dates = event.startsAt && event.endsAt
    ? formatEventDates(event.startsAt, event.endsAt, event.timeZone)
    : { dayLong: "Fecha por confirmar", dayShort: "Fecha por confirmar", timeRange: "Horario por confirmar", startTime: "" };
  const hasDates = Boolean(event.startsAt && event.endsAt);
  const venueName = event.venue.name ?? "Lugar por confirmar";
  const venueAddress = event.venue.address ?? "Dirección por confirmar";
  const L = t.layout;
  const split = L === 'split';
  const style = t.vars as CSSProperties;

  const registration = <RegistrationCard event={event} onRegister={onRegister} />;
  const organizer = (
    <div className="ev-organizer">
      <span className="ev-avatar" data-size="lg" aria-hidden="true">{event.organizer.initials}</span>
      <span className="ev-organizer-text">
        <span className="ev-small">Organiza</span>
        <span style={{ fontWeight: 600 }}>{event.organizer.name}</span>
      </span>
    </div>
  );
  const metaList = (
    <div className="ev-meta">
      <div className="ev-meta-row">
        <span className="ev-meta-icon"><IconCalendar size={20} /></span>
        <span className="ev-meta-text">
          <span className="ev-meta-strong">{dates.dayLong}</span>
          <span className="ev-meta-sub ev-mono">{hasDates ? `${dates.timeRange} · ${zoneLabel(event.timeZone)}` : dates.timeRange}</span>
        </span>
      </div>
      <div className="ev-meta-row">
        <span className="ev-meta-icon"><IconPin size={20} /></span>
        <span className="ev-meta-text">
          <span className="ev-meta-strong">{venueName}</span>
          <span className="ev-meta-sub">{venueAddress}</span>
        </span>
      </div>
    </div>
  );

  return (
    <div className="ev-page" data-style={t.style} data-layout={L} style={style}>
      {t.background !== 'ribbons' && <EventBackground kind={t.background} motion={motion} />}

      <div className="ev-content">
        {L === 'band' ? (
          <header className="ev-band">
            <div className="ev-container ev-topbar">
              <span className="ev-org">
                <span className="ev-avatar" aria-hidden="true">{event.organizer.initials}</span>
                <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
                  <span style={{ fontWeight: 600, fontSize: 16 }}>{event.organizer.name}</span>
                  {event.organizer.tagline ? <span style={{ fontSize: 13, opacity: 0.85 }}>{event.organizer.tagline}</span> : null}
                </span>
              </span>
              <ShareButton onShare={onShare} />
            </div>
          </header>
        ) : (
          <header className="ev-container ev-topbar">
            <span className="ev-org">
              <span className="ev-avatar" aria-hidden="true">{event.organizer.initials}</span>
              {event.organizer.name}
            </span>
            <ShareButton onShare={onShare} />
          </header>
        )}

        {L === 'poster' && (
          <section className="ev-hero">
            <EventBackground kind="ribbons" motion={motion} />
            <div className="ev-container ev-hero-inner">
              <div className="ev-hero-text">
                {event.kicker ? <span className="ev-kicker">{event.kicker}</span> : null}
                <h1 className="ev-title">{event.title}</h1>
                <div className="ev-hero-meta">
                  <span>{dates.startTime ? `${dates.dayShort} · ${dates.startTime}` : dates.dayShort}</span>
                  <span>{venueName}</span>
                </div>
              </div>
              <div className="ev-hero-cover"><EventCover shape="square" imageUrl={event.coverImageUrl} /></div>
            </div>
          </section>
        )}

        <main className="ev-container ev-main">
          {L === 'stack' && (
            <div className="ev-stack-head">
              {event.kicker ? <span className="ev-kicker">{event.kicker}</span> : null}
              <h1 className="ev-title">{event.title}</h1>
              {event.summary ? <p className="ev-lede">{event.summary}</p> : null}
            </div>
          )}

          {(L === 'stack' || L === 'band') && <EventCover shape="wide" imageUrl={event.coverImageUrl} />}

          <div className="ev-columns">
            {split && (
              <aside className="ev-aside" data-side="left" aria-label="Inscripción y organización">
                <EventCover shape="square" imageUrl={event.coverImageUrl} />
                <div className="ev-wide">{registration}</div>
                <div className="ev-wide">{organizer}</div>
              </aside>
            )}

            <div className="ev-body">
              {(split || L === 'band') && <h1 className="ev-title">{event.title}</h1>}
              {split && metaList}
              {L === 'stack' && <div className="ev-narrow">{metaList}</div>}
              {L === 'band' && (
                <div className="ev-facts">
                  <Fact label="Fecha" value={dates.dayLong} />
                  <Fact label="Horario" value={dates.timeRange} />
                  <Fact label="Lugar" value={venueName} />
                </div>
              )}

              <div className="ev-narrow">{registration}</div>

              {event.participation.length > 0 && (
                <section className="ev-section" aria-labelledby="ev-participa">
                  <h2 id="ev-participa" className="ev-h2">Participa desde tu celular</h2>
                  <div className="ev-chips">
                    {event.participation.map((tool) => <Chip key={tool} icon={TOOL_LABEL[tool].icon}>{TOOL_LABEL[tool].label}</Chip>)}
                  </div>
                  <p className="ev-muted">El día del evento escaneas un código y participas sin descargar nada.</p>
                </section>
              )}

              {event.about.length > 0 && (
                <section className="ev-section" aria-labelledby="ev-sobre">
                  <h2 id="ev-sobre" className="ev-h2">Sobre el evento</h2>
                  {event.about.map((p) => <p key={p}>{p}</p>)}
                </section>
              )}

              {event.agenda.length > 0 && (
                <section className="ev-section" aria-labelledby="ev-programa">
                  <h2 id="ev-programa" className="ev-h2">Programa</h2>
                  <ol className="ev-agenda" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                    {event.agenda.map((a) => (
                      <li key={`${a.time}-${a.title}`} className="ev-agenda-row">
                        <span className="ev-agenda-time">{a.time}</span>
                        <span className="ev-agenda-what">
                          <span>{a.title}</span>
                          {a.detail ? <span className="ev-agenda-detail">{a.detail}</span> : null}
                        </span>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              <section className="ev-section" aria-labelledby="ev-llegar">
                <h2 id="ev-llegar" className="ev-h2">Cómo llegar</h2>
                <div className="ev-map"><MapSketch label={`Mapa de referencia de ${venueName}`} /></div>
                <div className="ev-row-between">
                  <span className="ev-muted">{venueAddress}{event.venue.note ? ` · ${event.venue.note}` : ''}</span>
                  {event.venue.mapUrl ? <a className="ev-link" href={event.venue.mapUrl} target="_blank" rel="noopener noreferrer">Abrir en mapas</a> : null}
                </div>
              </section>

              {/* En celular, quién organiza va al final (en escritorio vive en la columna lateral). */}
              <div className="ev-narrow">{organizer}</div>
            </div>

            {!split && (
              <aside className="ev-aside ev-wide" data-side="right" aria-label="Inscripción y organización">
                {L === 'stack' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 18, borderBottom: '1px solid var(--ev-line)' }}>
                    <span style={{ fontWeight: 600, fontSize: 18 }}>{dates.dayLong}</span>
                    <span className="ev-muted">{dates.timeRange} · {venueName}, {venueAddress}</span>
                  </div>
                )}
                {registration}
                {organizer}
                {event.accessibility.length > 0 && <span className="ev-small">{event.accessibility.join(' · ')}</span>}
              </aside>
            )}
          </div>
        </main>

        <footer className="ev-footer">
          <div className="ev-container">
            <span>Tus datos se tratan según la Ley 21.719. <a href={event.privacyUrl}>Política de privacidad</a></span>
            <span>Hecho con Cuórum</span>
          </div>
        </footer>
      </div>
    </div>
  );
}

function ShareButton({ onShare }: { onShare?: () => void }) {
  return (
    <button type="button" className="ev-share" onClick={onShare}>
      <IconShare size={16} />
      Compartir
    </button>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="ev-fact">
      <span className="ev-fact-label">{label}</span>
      <span className="ev-fact-value">{value}</span>
    </div>
  );
}

/** Tarjeta de inscripción. Cada estado dice qué pasa y qué puede hacer la persona. */
export function RegistrationCard({ event, onRegister }: { event: EventPageData; onRegister?: () => void }) {
  const cap = event.capacity;
  const left = cap ? Math.max(0, cap.total - cap.taken) : null;
  const state = event.registration;

  const copy: Record<RegistrationState, { title: string; note: string; cta: string | null }> = {
    open: { title: 'Inscripción gratuita', note: 'Te enviamos la entrada con QR y un recordatorio el día antes.', cta: 'Inscribirme' },
    full: { title: 'Cupos agotados', note: 'Si alguien libera su cupo, te avisamos por correo en orden de llegada.', cta: 'Unirme a la lista de espera' },
    closed: { title: 'Inscripción cerrada', note: 'La organización cerró la inscripción para este evento.', cta: null },
    cancelled: { title: 'Evento cancelado', note: 'Las personas inscritas recibieron un aviso por correo.', cta: null },
    past: { title: 'Este evento ya pasó', note: 'Gracias por tu interés. Revisa los próximos eventos de la organización.', cta: null },
  };
  const c = copy[state];

  return (
    <div className="cq-card ev-register" data-state={state} aria-live="polite">
      <div className="ev-register-head">
        <span className="ev-register-title">{c.title}</span>
        {cap && state === 'open' ? <span className="ev-register-note">Quedan {left} de {cap.total}</span> : null}
      </div>
      {cap && (state === 'open' || state === 'full') ? (
        <Progress value={cap.taken} max={cap.total} label={`${cap.taken} de ${cap.total} cupos tomados`} />
      ) : null}
      {c.cta ? <Button size="lg" block onClick={onRegister}>{c.cta}</Button> : null}
      <span className="ev-register-note">{c.note}</span>
    </div>
  );
}

function MapSketch({ label }: { label: string }) {
  return (
    <svg viewBox="0 0 800 260" preserveAspectRatio="xMidYMid slice" role="img" aria-label={label}>
      <rect width="800" height="260" style={{ fill: 'var(--ev-surface)' }} />
      <path d="M0 80 H800 M0 190 H800 M150 0 V260 M420 0 V260 M650 0 V260" style={{ stroke: 'var(--ev-line)' }} strokeWidth="18" />
      <path d="M0 260 L300 0" style={{ stroke: 'var(--ev-line)' }} strokeWidth="10" />
      <circle cx="420" cy="132" r="14" style={{ fill: 'var(--ev-bar)' }} />
      <circle cx="420" cy="132" r="28" fill="none" style={{ stroke: 'var(--ev-bar)' }} strokeWidth="3" opacity=".5" />
    </svg>
  );
}
